/**
 * Application dialogs.
 *
 * `window.confirm` and `window.prompt` are unusable for anything that matters:
 * they cannot be styled, they cannot carry a paragraph explaining what the
 * action does, and on a destructive action the explanation IS the safeguard.
 * They also block the whole browser and look like a phishing prompt.
 *
 * SweetAlert2 was already a dependency here, so this wraps it in the app's
 * palette once rather than every page reaching for it and picking its own
 * colours.
 */

import Swal from 'sweetalert2';

const TEAL = '#145f5f';
const GOLD = '#8a6a1f';

/** Styling lives in styles/dialog.css so it can match Modal.jsx exactly. */
const classes = (tone) => ({
  container: 'cs-swal__container',
  popup: `cs-swal${tone === 'danger' ? ' cs-swal--danger' : ''}`,
  title: 'cs-swal__title',
  htmlContainer: 'cs-swal__html',
  inputLabel: 'cs-swal__label',
  input: 'cs-swal__input',
  actions: 'cs-swal__actions',
  confirmButton: 'cs-swal__confirm',
  cancelButton: 'cs-swal__cancel',
  validationMessage: 'swal2-validation-message',
});

const base = (tone) => ({
  buttonsStyling: false,
  reverseButtons: true,
  focusConfirm: false,
  showClass: { popup: '' },
  hideClass: { popup: '' },
  customClass: classes(tone),
});

/** Escape anything that reaches Swal's `html`, which does not escape by itself. */
export const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/**
 * Ask before doing something. `tone: 'danger'` for anything that cannot simply
 * be undone.
 *
 * @returns {Promise<boolean>}
 */
export async function confirmAction({ title, message, confirmText = 'Confirm', cancelText = 'Cancel', tone = 'default' }) {
  const res = await Swal.fire({
    ...base(tone),
    title,
    html: message,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    // No icon. A dialog whose whole job is to explain a consequence should be
    // read, and a large exclamation mark just trains people to click past it.
    // The gold top border carries the warning instead.
    iconColor: tone === 'danger' ? GOLD : TEAL,
  });
  return res.isConfirmed;
}

/**
 * Ask for one value. Returns the string, or null when dismissed.
 *
 * `validate` returns an error message to keep the dialog open, or nothing to
 * accept — so a bad date is caught here rather than by the server.
 */
export async function promptFor({
  title, message, label, initial = '', placeholder = '', inputType = 'text',
  confirmText = 'Save', tone = 'default', validate,
}) {
  const res = await Swal.fire({
    ...base(tone),
    title,
    html: message || undefined,
    input: inputType,
    inputLabel: label,
    inputValue: initial,
    inputPlaceholder: placeholder,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: 'Cancel',
    inputValidator: validate ? (v) => validate(v) || undefined : undefined,
  });
  return res.isConfirmed ? res.value : null;
}

/** A date, validated before it leaves the dialog. */
export function promptForDate(opts) {
  return promptFor({
    ...opts,
    inputType: 'date',
    initial: opts.initial || new Date().toISOString().slice(0, 10),
    validate: (v) => {
      if (!v) return 'Pick a date';
      if (Number.isNaN(new Date(v).getTime())) return 'That is not a date';
      return opts.validate ? opts.validate(v) : undefined;
    },
  });
}

/** Tell the user something happened. */
export function notify({ title, message, tone = 'success' }) {
  return Swal.fire({
    ...base(tone === 'success' ? 'default' : 'danger'),
    title,
    html: message,
    confirmButtonText: 'Close',
  });
}

const dialog = { confirmAction, promptFor, promptForDate, notify, esc };
export default dialog;
